import { itemTaxRate, ivaIncluded } from "./docTax";
// 🏬 Wholesale A4 invoice (SENIAT-style layout) — Nota de Entrega / Factura format
// Prints a full-page document with RIF, customer block, item table (Código/Descripción/
// Cantidad/Precio/IVA/Total) and Base Imponible / Exento / IVA / TOTAL footer in Bs.

// 🎰 Gran Sorteo line for Pedido/Nota (NOT Factura — keep fiscal docs clean for SENIAT)
const sorteoDocLine = (order) => {
  const so = order?.sorteo;
  if (!so || !so.count) return "";
  const dd = so.draw_date ? ` · Sorteo: ${esc(String(so.draw_date).slice(0, 10).split("-").reverse().join("/"))}` : "";
  return `<div style="text-align:center;font-weight:bold;margin-top:4px">🎟️ ${esc(so.campaign || "GRAN SORTEO")}: ${so.count} BOLETO${so.count > 1 ? "S" : ""} · N° ${esc(so.first)}${so.count > 1 ? ` AL ${esc(so.last)}` : ""}${dd}</div>`;
};

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fmtBs = (v) => Number(v || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// 👤 开单员 — the logged-in employee who issues/prints the document
const issuerName = () => {
  try {
    if (typeof localStorage === "undefined") return "";
    const u = JSON.parse(localStorage.getItem("cached_auth_user") || "null");
    return u?.name || u?.username || "";
  } catch { return ""; }
};

// 📱 Webshop QR (same source as the 80mm receipt QR) — respects receipt_qr_enabled
const shopQrHtml = (settings, sizeMm = 16) => {
  if (settings?.receipt_qr_enabled === false) return "";
  try {
    // eslint-disable-next-line global-require
    const { qrPng } = require("./receiptPrinter");
    const u = typeof localStorage !== "undefined" ? JSON.parse(localStorage.getItem("cached_auth_user") || "{}") : {};
    if (!u.tenant_id && !settings?.shop_slug) return "";
    const url = settings?.shop_slug ? `https://${settings.shop_slug}.sellox.shop` : `https://sellox.shop/shop/${u.tenant_id}`;
    const svg = qrPng(url, sizeMm);
    if (!svg) return "";
    return `<div style="text-align:center">${svg}<div style="font-size:7.5px">${esc(url.replace("https://", ""))}</div><div style="font-size:7.5px">Escanea y pide online</div></div>`;
  } catch { return ""; }
};

const A4_CSS = `
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; margin: 0; padding: 12mm; }
  .hdr { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 8px; }
  .hdr h1 { font-size: 20px; margin: 0 0 2px; }
  .hdr .meta { text-align: right; font-size: 12px; }
  .doc-title { font-size: 15px; font-weight: bold; border: 2px solid #000; padding: 4px 14px; display: inline-block; margin-top: 4px; }
  .cust { border: 1px solid #000; margin-top: 10px; padding: 6px 10px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; }
  table.items { width: 100%; border-collapse: collapse; margin-top: 10px; }
  table.items th { border: 1px solid #000; background: #eee; padding: 4px 6px; font-size: 11px; }
  table.items td { border: 1px solid #000; padding: 4px 6px; font-size: 11.5px; }
  .r { text-align: right; } .c { text-align: center; }
  .totals { margin-top: 8px; margin-left: auto; width: 300px; border-collapse: collapse; }
  .totals td { padding: 3px 8px; font-size: 12px; }
  .totals .grand td { border-top: 2px solid #000; font-size: 15px; font-weight: bold; }
  .foot { margin-top: 18px; font-size: 10.5px; color: #333; display: flex; justify-content: space-between; }
  .sign { margin-top: 40px; display: flex; gap: 60px; }
  .sign div { flex: 1; border-top: 1px solid #000; text-align: center; padding-top: 4px; font-size: 11px; }
`;


// SKU/variant text of a line, whichever channel wrote it (POS: variant · wholesale/vendedor/shop: sku_label/sku_key · legacy: product_spec)
export const lineVariant = (it) => {
  const v = it?.variant || it?.variants || it?.sku_label || it?.sku_key || "";
  if (!v) return "";
  // whole-token match only: "#52" in the name must NOT swallow colour "52" (Leyenda P1-52, 2026-10-14)
  const re = new RegExp("(^|[\\s(/])" + String(v).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?=$|[\\s)/])", "i");
  return re.test(String(it?.product_name || "")) ? "" : String(v);
};
// Nota/Factura description suffix: explicit spec, else the SKU variant
const notaSpec = (it) => it?.product_spec || lineVariant(it);

export function buildWholesaleA4Html(order, settings, exchangeRates, storeInfo, docType) {
  const DOC_TITLES = { nota: "NOTA DE ENTREGA", factura: "FACTURA" };
  const docTitle = DOC_TITLES[docType] || "NOTA DE ENTREGA / PEDIDO";
  const rate = order.bcv_rate || exchangeRates?.sales_exchange_rate || exchangeRates?.usd_to_ves || 0;
  const incl = ivaIncluded(settings);
  const items = order.items || [];
  let base = 0, exento = 0, iva = 0;
  const rows = items.map((it, i) => {
    const r = itemTaxRate(it, settings) / 100;
    const usdAmt = (it.amount || (it.unit_price || 0) * (it.quantity || 0));
    const bsAmt = it.unit_price_bs ? it.unit_price_bs * (it.quantity || 0) : usdAmt * rate;
    const unitBs = it.unit_price_bs || (it.unit_price || 0) * rate;
    let lineBase = bsAmt, lineIva = 0;
    if (r > 0) {
      lineBase = incl ? bsAmt / (1 + r) : bsAmt;
      lineIva = incl ? bsAmt - lineBase : bsAmt * r;
      base += lineBase; iva += lineIva;
    } else {
      exento += bsAmt;
    }
    return `<tr>
      <td class="c">${i + 1}</td>
      <td class="c">${esc(it.product_code || it.code || "")}</td>
      <td>${esc(it.product_name || "")}${lineVariant(it) ? " (" + esc(lineVariant(it)) + ")" : ""}</td>
      <td class="c">${Number(it.quantity || 0)}${Number(it.boxes) > 0 ? ` 📦${Number(it.boxes)}cj` : ""}</td>
      <td class="r">${fmtBs(incl && r > 0 ? unitBs / (1 + r) : unitBs)}</td>
      <td class="c">${r > 0 ? (itemTaxRate(it, settings) + "%") : "E"}</td>
      <td class="r">${fmtBs(lineIva)}</td>
      <td class="r">${fmtBs(r > 0 ? lineBase + lineIva : bsAmt)}</td>
    </tr>`;
  }).join("");
  const totalBs = base + exento + iva;
  const totalUsd = rate > 0 ? totalBs / rate : (order.total_amount || 0);
  const d = order.created_at ? new Date(order.created_at) : new Date();
  const fecha = d.toLocaleDateString("es-VE") + " " + d.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" });
  const qty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);

  const body = `
  ${Number(settings?.a4_top_margin_mm) > 0 ? `<div style="height:${Number(settings.a4_top_margin_mm)}mm"></div>` : ""}
  <div class="hdr"${settings?.a4_preprinted === true ? ' style="border-bottom:none"' : ""}>
    ${settings?.a4_preprinted === true ? "<div></div>" : `<div>
      <h1>${esc(settings?.company_name || storeInfo?.name || settings?.shop_name || "")}</h1>
      <div>RIF: ${esc(settings?.company_tax_id || "")}</div>
      <div>${esc(settings?.company_address || "")}</div>
      <div>Telf: ${esc(settings?.company_phone || "")}</div>
    </div>`}
    <div class="meta">
      ${docType === "factura" ? '<div style="font-size:12px;font-weight:bold;margin-bottom:3px">N° DE CONTROL 00-____________</div>' : ""}
      <div class="doc-title">${docTitle}</div>
      <div style="margin-top:6px">N°: <b>${esc(order.order_no || "")}</b></div>
      <div>Fecha: ${fecha}</div>
      <div>Tasa BCV: Bs ${fmtBs(rate)} / USD</div>
    </div>
  </div>
  <div class="cust">
    <div><b>Cliente:</b> ${esc(order.customer_name || order.shipping_name || "CONTADO")}</div>
    <div><b>RIF/CI:</b> ${esc(order.customer_rif || "")}</div>
    <div><b>Teléfono:</b> ${esc(order.customer_phone || order.shipping_phone || "")}</div>
    <div><b>Dirección:</b> ${esc(order.customer_address || order.shipping_address || "")}</div>
    <div><b>Condición:</b> ${order.payment_method === "credit" ? "CRÉDITO" : "CONTADO"}</div>
    <div><b>Vendedor:</b> ${esc(order.cashier_name || order.cashier || "")}</div>
  </div>
  <table class="items">
    <thead><tr>
      <th style="width:24px">#</th><th style="width:70px">Código</th><th>Descripción</th>
      <th style="width:50px">Cant.</th><th style="width:80px">Precio Bs</th>
      <th style="width:45px">% IVA</th><th style="width:75px">IVA Bs</th><th style="width:90px">Total Bs</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <table class="totals">
    <tr><td>Total artículos:</td><td class="r">${qty}</td></tr>
    <tr><td>Base Imponible (Gravado):</td><td class="r">Bs ${fmtBs(base)}</td></tr>
    <tr><td>Exento (E):</td><td class="r">Bs ${fmtBs(exento)}</td></tr>
    <tr><td>Subtotal:</td><td class="r">Bs ${fmtBs(base + exento)}</td></tr>
    <tr><td>IVA 16%:</td><td class="r">Bs ${fmtBs(iva)}</td></tr>
    <tr class="grand"><td>TOTAL:</td><td class="r">Bs ${fmtBs(totalBs)}</td></tr>
    <tr><td style="color:#555">Ref. USD:</td><td class="r" style="color:#555">$ ${totalUsd.toFixed(2)}</td></tr>
  </table>
  <div class="sign">
    <div>Entregado por</div>
    ${shopQrHtml(settings)}
    <div>Recibido por (firma y sello)</div>
  </div>
  <div class="foot">
    <span>${docType === "factura"
      ? "ESTA FACTURA VA SIN TACHADURAS NI ENMENDADURAS"
      : docType === "nota"
        ? "Nota de entrega — COPIA SIN DERECHO A CRÉDITO FISCAL. Precios en Bolívares (BCV)."
        : "Documento no fiscal — Nota de entrega. Precios expresados en Bolívares (BCV)."}</span>
    <span>Sellox POS</span>
  </div>
  ${settings?.a4_footer_text ? `<div style="text-align:center;font-size:10px;margin-top:6px">${esc(settings.a4_footer_text)}</div>` : ""}`;
  const accent = settings?.a4_accent_color || "";
  const extra = accent ? `<style>.hdr{border-bottom-color:${esc(accent)}!important}.hdr h1{color:${esc(accent)}}table.items th{background:${esc(accent)}22}</style>` : "";
  return "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><style>" + A4_CSS + "</style>" + extra + fontCss(settings) + "</head><body>" + body + "</body></html>";
}

export function printWholesaleA4(order, settings, exchangeRates, storeInfo, printOpts, docType) {
  const html = buildWholesaleA4Html(order, settings, exchangeRates, storeInfo, docType);
  const dialogPrint = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.error("A4 print:", e); } }, 400);
  };
  // Desktop: always print through the shell (named printer or Windows default) so the exact paper size
  // declared in the HTML reaches the driver (A5 stays A5 — no centering on Letter). Dialog only as fallback.
  if (typeof window !== "undefined" && window.selloxDesktop?.printHTML) {
    window.selloxDesktop.printHTML(html, printOpts?.printerName || settings?.report_printer_name || "")
      .then((r) => { if (!r || !r.ok) dialogPrint(); }).catch(dialogPrint);
    return;
  }
  dialogPrint();
}

// 📋 Pedido A5 — exact replica of the boss's paper picking slip (photo 2026-06):
// "Fecha + N° + total" top line, CLIENTE/DIRECCION/RIF-TELEFONO/VENDEDORES block,
// CANTIDAD | DESCRIPCION rule, rows = qty + unit + description. No prices per line.
// a5c = A5 sheet in a Letter/A4 tray (Canon etc.): @page auto = the driver's own paper (no mismatch prompt, and NO fit-to-page scaling/centering that pushed the content ~15mm down), content in a 148mm column centered horizontally, top-aligned
// 📄 Paper catalogue shared by Pedido / Nota / Factura: css = @page size, mm = exact size handed to the Windows driver
export const DOC_PAPER = {
  a5: { css: "A5 portrait", mm: "148x210" },
  half: { css: "5.5in 8.5in", mm: "140x216" },
  letter: { css: "letter portrait", mm: "215.9x279.4" },
  a4: { css: "A4 portrait", mm: "210x297" },
};
const pedidoPage = (settings) => ({ auto: "auto", letter: "letter portrait", half: "5.5in 8.5in", a4: "A4 portrait", a5c: "auto" }[settings?.pedido_paper] || "A5 portrait");
const A5_CSS = `
  @page { size: A5 portrait; margin: 0; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; margin: 0; padding: 8mm; }
  .co { text-align: center; margin-bottom: 5px; }
  .co h1 { font-size: 16px; letter-spacing: 2px; margin: 0; font-weight: 700; }
  .co div { font-size: 10px; }
  .top { display: flex; justify-content: space-between; align-items: baseline; font-size: 12.5px; }
  .top .no { font-weight: bold; text-decoration: underline; margin-left: 6px; }
  .top .tot { font-weight: bold; font-size: 14px; text-decoration: underline; }
  .cust { margin-top: 4px; font-size: 11.5px; line-height: 1.55; }
  .cust b { font-weight: bold; }
  .cols { display: flex; font-weight: bold; font-size: 11.5px; margin-top: 6px; border-bottom: 1.5px solid #000; padding-bottom: 2px; }
  .cols .q { width: 90px; } 
  table.p { width: 100%; border-collapse: collapse; }
  table.p td { padding: 5px 2px 5px 0; font-size: 12.5px; vertical-align: top; }
  table.p thead { display: table-header-group; }
  table.p th.hdr { font-weight: normal; text-align: left; padding: 0 0 2px 0; }
  table.p th.hdr .cols { margin-bottom: 2px; }
  table.p tr { break-inside: avoid; page-break-inside: avoid; }
  .qty { width: 42px; font-weight: bold; text-align: left; }
  .un { width: 48px; }
`;

// 🏷️ Custom label overrides (doc designer): tenants rename fixed labels
const lbl = (settings, key, def) => esc((((settings || {}).doc_labels || {})[key] || def));

// 🔤 Tenant-selectable document font (doc designer 字体)
const fontCss = (settings) => settings?.doc_font ? `<style>body, td, th, div, span { font-family: ${esc(settings.doc_font)}, Arial, sans-serif !important; }</style>` : "";

// 🧲 Per-block layout offsets (doc designer drag-move): dx/dy in mm + zoom %
function blkAttr(settings, doc, id, extraStyle = "") {
  const o = (((settings || {}).b2b_layout || {})[doc] || {})[id] || {};
  const dx = Number(o.dx) || 0, dy = Number(o.dy) || 0, fs = Number(o.fs) || 100;
  let st = extraStyle;
  if (dx || dy) st += `position:relative;left:${dx}mm;top:${dy}mm;`;
  if (fs !== 100) st += `zoom:${fs / 100};`;
  return st ? ` style="${st}"` : "";
}

// name + variant + spec + code, without repeating a SKU label the name already carries
export function pedidoDesc(it) {
  const name = it.product_name || "";
  const variant = lineVariant(it);
  const parts = [name];
  if (variant && !name.includes(variant)) parts.push("(" + variant + ")");
  if (it.product_spec && it.product_spec !== variant && !name.includes(it.product_spec)) parts.push(it.product_spec);
  if (it.product_code && !name.includes(it.product_code)) parts.push(it.product_code);
  return parts.join(" ");
}

export function buildPedidoA5Html(order, settings, opts = {}) {
  const items = order.items || [];
  const d = order.created_at ? new Date(order.created_at) : new Date();
  const fecha = d.toLocaleDateString("es-VE");
  const rate = Number(opts.rate) > 0 ? Number(opts.rate) : Number(order.bcv_rate || 0);
  const retUsd = Number(order.return_amount || 0);
  const usdTot = Math.max(0, Number(order.total_amount || 0) - retUsd);
  let totalTxt;
  if (opts.currency === "USD") totalTxt = `$ ${usdTot.toFixed(2)}`;
  else if (opts.currency === "RMB" && Number(opts.rmbRate) > 0) totalTxt = `¥ ${(usdTot * Number(opts.rmbRate)).toFixed(2)}`;
  else {
    const totBs = retUsd > 0 && rate > 0 ? usdTot * rate : (order.total_amount_bs || (rate > 0 ? usdTot * rate : 0));
    totalTxt = totBs > 0 ? fmtBs(totBs) : `$ ${usdTot.toFixed(2)}`;
  }
  const rows = items.map((it) => `<tr>
    <td class="qty">${Number(it.quantity || 0)}${Number(it.boxes) > 0 ? ` 📦${Number(it.boxes)}cj` : ""}</td>
    <td class="un">${esc(lineUnit(it))}</td>
    <td>${esc(pedidoDesc(it))}</td>
  </tr>`).join("");
  const coName = (settings?.company_name || settings?.shop_name || "").toUpperCase();
  const fs = settings?.pedido_font_size;
  const pt = fs === "large" ? 14.5 : (Number(fs) >= 9 && Number(fs) <= 24 ? Number(fs) : 0);
  const bigCss = pt ? `<style>body{font-size:${pt}px} table.p td{font-size:${pt + 1}px} .cust{font-size:${pt - 0.5}px} .top{font-size:${pt + 0.5}px} .top .tot{font-size:${pt + 2.5}px} .cols{font-size:${pt - 0.5}px}</style>` : "";
  const header = `
  ${(coName && !settings?.pedido_hide_co) ? `<div class="co"${blkAttr(settings, "pedido", "co")}>
    <h1>${esc(coName)}</h1>
    ${settings?.company_tax_id ? `<div>RIF: ${esc(settings.company_tax_id)}</div>` : ""}
    ${settings?.company_phone ? `<div>Telf.: ${esc(settings.company_phone)}</div>` : ""}
  </div>` : ""}
  ${settings?.pedido_header_text ? `<div${blkAttr(settings, "pedido", "hdrx", "text-align:center;font-size:11px;white-space:pre-wrap;margin-bottom:4px;")}>${esc(settings.pedido_header_text)}</div>` : ""}
  <div class="top"${blkAttr(settings, "pedido", "top")}>
    <span>${lbl(settings, "pd_fecha", "Fecha:")}${fecha} <span class="no">${esc(order.order_no || "")}</span></span>
    <span class="tot">${totalTxt}</span>
  </div>
  <div class="cust"${blkAttr(settings, "pedido", "cust")}>
    <div><b>${lbl(settings, "pd_cliente", "CLIENTE:")}</b> ${esc(order.customer_name || order.shipping_name || "")}</div>
    <div><b>${lbl(settings, "pd_direccion", "DIRECCION:")}</b> ${esc(order.customer_address || order.shipping_address || "")}</div>
    <div><b>${lbl(settings, "pd_rif", "RIF:")}</b> ${esc(order.customer_rif || order.rif || "")} <b>${lbl(settings, "pd_tel", "TELEFONO:")}</b> ${esc(order.customer_phone || order.shipping_phone || "")}</div>
    <div><b>${lbl(settings, "pd_vendedor", "VENDEDORES:")}</b> ${esc(order.vendedor || order.salesman_name || "")} &nbsp; <b>${lbl(settings, "pd_emit", "EMITIDO POR:")}</b> ${esc(issuerName())}${order.notes ? ` &nbsp; <b>NOTA:</b> ${esc(order.notes)}` : ""}</div>
    ${retUsd > 0 ? `<div style="font-weight:bold"><b>DEVOLUCION/ROTURA:</b> -$${retUsd.toFixed(2)}${rate > 0 && opts.currency !== "USD" ? ` (${fmtBs(retUsd * rate)})` : ""} &nbsp; Total original: $${Number(order.total_amount || 0).toFixed(2)}</div>` : ""}
  </div>
  <div class="cols"><span class="q">${lbl(settings, "pd_h_cant", "CANTIDAD")}</span><span>${lbl(settings, "pd_h_desc", "DESCRIPCION")}</span></div>
`;
  // 📄 Every page repeats the shop/customer header + column titles: <thead> is re-printed by Chromium on each
  // page break (Leyenda 2026-10-14: page 2 of a long pedido came out as bare lines with no cabecera).
  const repeatHdr = settings?.pedido_repeat_header !== false;
  const body = repeatHdr ? `
  <div${blkAttr(settings, "pedido", "table")}>
  <table class="p"><thead><tr><th class="hdr" colspan="3">${header}</th></tr></thead><tbody>${rows}</tbody></table>
  </div>
  ${settings?.pedido_footer_text ? `<div${blkAttr(settings, "pedido", "footer", "text-align:center;margin-top:10px;font-size:11px;white-space:pre-wrap;")}>${esc(settings.pedido_footer_text)}</div>` : ""}${sorteoDocLine(order)}` : `
  ${header}
  <div${blkAttr(settings, "pedido", "table")}>
  <table class="p"><tbody>${rows}</tbody></table>
  </div>
  ${settings?.pedido_footer_text ? `<div${blkAttr(settings, "pedido", "footer", "text-align:center;margin-top:10px;font-size:11px;white-space:pre-wrap;")}>${esc(settings.pedido_footer_text)}</div>` : ""}${sorteoDocLine(order)}`;
  // Bottom margin keeps the last row out of the printer's ~4mm dead zone (Leyenda 2026-10-15: "最尾那横吃掉"); rows never split across pages
  const bottomMm = settings?.pedido_bottom_mm !== null && settings?.pedido_bottom_mm !== undefined && settings?.pedido_bottom_mm !== "" ? Number(settings.pedido_bottom_mm) : 7;
  const pageCss = `<style>@page { size: ${pedidoPage(settings)}; margin: 0 0 ${bottomMm}mm 0; } table.p tr { break-inside: avoid; page-break-inside: avoid; }</style>`;
  // tell the desktop shell the exact paper (mm) so the Windows driver gets A5 (no centering on Letter)
  const PAPER_MM = { a5: DOC_PAPER.a5.mm, half: DOC_PAPER.half.mm, letter: DOC_PAPER.letter.mm, a4: DOC_PAPER.a4.mm };
  const a5c = settings?.pedido_paper === "a5c";
  // a5c: the JOB paper must equal what the printer has REGISTERED (Canon rejects any mismatch). Use the
  // "报表打印机 → 纸张尺寸" field for that (Letter/A4/Legal) instead of trusting the Windows driver default.
  const regPaper = { letter: DOC_PAPER.letter.mm, carta: DOC_PAPER.letter.mm, a4: DOC_PAPER.a4.mm, legal: "216x356", oficio: "216x356", a5: DOC_PAPER.a5.mm }[String(settings?.report_paper_size || "letter").toLowerCase()] || DOC_PAPER.letter.mm;
  const metaMm = a5c ? regPaper : PAPER_MM[settings?.pedido_paper || "a5"];
  const paperMeta = metaMm ? `<meta name="sellox-page-size" content="${metaMm}">` : "";
  // Top margin (mm) — default 5: printers have a ~4mm hard margin, 0 chopped the first line (andy, Oficio paper)
  const topMm = settings?.pedido_top_mm !== null && settings?.pedido_top_mm !== undefined && settings?.pedido_top_mm !== "" ? Number(settings.pedido_top_mm) : 5;
  const bodyHtml = a5c
    ? `<div style="width:148mm;margin:${topMm}mm ${settings?.pedido_align === "left" ? "0" : "auto"} 0;padding:0 8mm;box-sizing:border-box">${body}</div>` // boss: centered (default); pedido_align=left available if the column falls off a small sheet
    : `<div style="margin-top:${topMm}mm">${body}</div>`;
  const a5cCss = a5c ? "<style>body{padding:0}</style>" : "<style>body{padding-top:0}</style>";
  return "<!DOCTYPE html><html><head><meta charset=\"utf-8\">" + paperMeta + "<style>" + A5_CSS + "</style>" + pageCss + a5cCss + bigCss + fontCss(settings) + "</head><body>" + bodyHtml + "</body></html>";
}

export function printPedidoA5(order, settings, printOpts, opts) {
  if (dotMatrixTxtOn(printOpts?.printerName, settings)) {
    import("./dotMatrixText").then(m => m.printPedidoTxt(order, settings, { ...(printOpts || {}), printerName: printOpts?.printerName || settings?.report_printer_name || "" }, opts)
      .catch(async (e) => { const { busToast } = await import("./toastBus"); busToast("error", "🖨️ TXT: " + (e.message || e)); }));
    return;
  }
  const html = buildPedidoA5Html(order, settings, opts || {});
  const dialogPrint = () => {
    const w = window.open("", "_blank", "width=700,height=900");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.error("A5 print:", e); } }, 400);
  };
  // Desktop: always print through the shell (named printer or Windows default) so the exact paper size
  // declared in the HTML reaches the driver (A5 stays A5 — no centering on Letter). Dialog only as fallback.
  if (typeof window !== "undefined" && window.selloxDesktop?.printHTML) {
    window.selloxDesktop.printHTML(html, printOpts?.printerName || settings?.report_printer_name || "")
      .then((r) => { if (!r || !r.ok) dialogPrint(); }).catch(dialogPrint);
    return;
  }
  dialogPrint();
}

// 📄🧾 Nota de Entrega / Factura — exact replicas of the boss's dot-matrix documents:
// centered company header, N° in red, cliente block with fecha/condición at right,
// (nota: CODIGO RMB DESC% T.RMB) DESCRIPCION CANTIDAD PRECIO U BS TOTAL BS,
// Total Neto / IVA 16% / Total a Pagar Bs, legend lines.
const NF_CSS = `
  @page { size: letter; margin: 0; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11.5px; color: #000; margin: 0; padding: 10mm 12mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; -webkit-font-smoothing: none; text-rendering: geometricPrecision; }
  .co { text-align: center; }
  .co h1 { font-size: 21px; letter-spacing: 3px; margin: 0; font-weight: 700; }
  .co div { font-size: 11px; }
  .ctrl { display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; margin-top: 8px; }
  .red { color: #c00; }
  .cli { margin-top: 4px; font-size: 11px; line-height: 1.55; display: flex; justify-content: space-between; gap: 16px; }
  .cli .l { flex: 1; }
  .cli .rmeta { text-align: left; white-space: nowrap; }
  .vend { display: flex; gap: 26px; font-size: 11px; margin-top: 2px; flex-wrap: wrap; }
  table.it { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.it thead th { border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; text-align: left; padding: 3px 4px; font-size: 10.5px; }
  table.it td { padding: 4px 4px 2px; font-size: 11px; vertical-align: top; }
  table.it .nw { white-space: nowrap; width: 1%; }
  .r { text-align: right; } .c { text-align: center; }
  .botwrap { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; }
  .botfix { position: fixed; bottom: 10mm; left: 12mm; right: 12mm; }
  .botend { margin-top: 6mm; }
  .obs { font-size: 10.5px; }
  .tot { width: 300px; font-size: 11.5px; border-top: 1px solid #000; padding-top: 4px; }
  .tot .row { display: flex; justify-content: space-between; padding: 1.5px 0; }
  .tot .big { font-weight: bold; font-size: 13.5px; }
  .legend { text-align: center; font-weight: bold; font-size: 11px; margin-top: 14px; }
  .mrco { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  .mrco h1 { font-size: 20px; margin: 0; font-weight: 700; letter-spacing: 1px; }
  .mrco .rif { font-size: 13px; font-weight: 700; margin-top: 2px; }
  .mrco .addr { text-align: center; font-weight: 700; font-size: 11px; max-width: 95mm; line-height: 1.5; }
  .mrcli { display: flex; justify-content: space-between; gap: 16px; margin-top: 14px; font-size: 11px; line-height: 1.45; }
  .mrcli .l { flex: 1; } .mrcli .k { display: inline-block; min-width: 24mm; } .mrcli .sp { margin-left: 18px; }
  .mrcli .rmeta { white-space: nowrap; text-align: right; }
  .mrobs { font-size: 11px; line-height: 1.6; }
  .mrline { border-top: 1px dashed #000; margin-bottom: 3px; }
  .mrtot { border-top: 0; font-size: 12.5px; font-weight: 700; }
  .mrtot .big { font-size: 15px; }
`;

// Which reference columns a Nota de Entrega (Bs) carries beside the Bs prices.
// Tenant setting b2b_ref_cols: "rmb" | "usd" | "none"; default = ¥ when the tenant has a RMB rate, otherwise $ REF (jhonny345 2026-10-05).
export function notaRefMode(settings, rmbRate, isNota, inUsd) {
  if (!isNota || inUsd) return "none";
  const m = settings?.b2b_ref_cols;
  if (m === "none" || m === "usd" || m === "rmb") return m;
  return Number(rmbRate) > 0 ? "rmb" : "usd";
}

export function buildNotaFacturaHtml(order, settings, rates, docType, rmbRate, opts = {}) {
  const isNC = docType === "nc";
  const isFactura = docType === "factura";
  const isNota = !isFactura && !isNC;
  // Leyenda 2026-10-18 (红笔批注): Nota 不打印合计框 / T RMB / 免责语 —— 金额店里手写
  const noNotaTotals = isNota && !!settings?.nf_nota_no_totals;
  const noRepeatRef = isNota && !!settings?.nf_nota_no_repeat_ref;
  // 🇻🇪 SENIAT Prov. 0071: forma libre paper is pre-printed by the imprenta with the
  // company letterhead (name/RIF/address) and the N° de control — software must NOT
  // reprint those, only the doc title, doc number and operation data. Default: pre-printed.
  // M&R 2026-10-31: Nota goes on plain Carta → print the company header (settings.b2b_nota_preprinted === false)
  const pre = opts.preprinted !== false && !(isNota && settings?.b2b_nota_preprinted === false);
  // 🧾 Factura 2 (Leyenda + M&R 2026-10-30): laser on the printer's pre-printed form — no QR, client block higher,
  // totals pulled up above the pre-printed footer, "ORIGINAL - CLIENTE" / "COPIA" legend per copy. Pedido/Nota/Factura untouched.
  const f2 = !!opts.factura2;
  // 🏭 M&R style (mayordemo 2026-10-31, owner's photos): header name/RIF left + address right, RAZON SOCIAL block,
  // columns Codigo · $ list · $ net · Descripcion · Cantidad · Precio BS "-2%" · Total BS, bottom-left REFERENCIA / -Descuento后 / CANTIDAD ITEMS,
  // SUBTOTAL Bs / IMPUESTO 16% / TOTAL Bs, plain "604274.10" numbers. Pedido untouched.
  const mr = settings?.b2b_layout_style === "mr" && !isNC;
  const num2 = (v) => Number(v || 0).toFixed(2);
  const fmtN = mr ? num2 : fmtBs;
  const inUsd = opts.currency === "USD";
  // Nota reference columns: "rmb" (¥ list price · desc% · ¥ total — La Leyenda) | "usd" (REF $ · desc% · TOTAL REF $ — Oniplast style) | "none"
  const refMode = notaRefMode(settings, rmbRate, isNota, inUsd);
  const showRmb = refMode !== "none";
  const refUsd = refMode === "usd";
  const rmbOnly = showRmb && settings?.b2b_rmb_only === true;
  const hid = (k) => !!((settings || {}).doc_hidden || {})[k];
  const rate = Number(opts.rate) > 0 ? Number(opts.rate) : Number(order.bcv_rate || rates?.sales_exchange_rate || rates?.usd_to_ves || 0);
  const catRates = opts.catRates || {};
  const itemCats = opts.itemCats || {};
  const incl = ivaIncluded(settings);
  const items = order.items || [];
  let base = 0, exento = 0, iva = 0, totalRmb = 0, totalUsdAll = 0, totalListUsd = 0;
  const rowObjs = items.map((it) => {
    const qty = Number(it.quantity || 0);
    const r = itemTaxRate(it, settings) / 100;
    const cat = (itemCats[it.product_id] || {}).category || "";
    const lineRate = Number(catRates[cat]) > 0 ? Number(catRates[cat]) : rate;
    const usdAmt = it.amount || (it.unit_price || 0) * qty;
    const amt = inUsd ? usdAmt : usdAmt * lineRate;
    const unitP = inUsd ? (it.unit_price || 0) : (it.unit_price || 0) * lineRate;
    let pB = 0, pI = 0, pEx = 0;
    if (r > 0) {
      const lb = incl ? amt / (1 + r) : amt;
      base += lb; iva += incl ? amt - lb : amt * r;
      pB = lb; pI = incl ? amt - lb : amt * r;
    } else { exento += amt; pEx = amt; }
    // legacy columns: RMB = list price · DESC = box discount % · T. RMB = net line total
    const discPct = Number(it.box_discount_pct) || Number(it.discount) || 0;
    const listUsd = Number(it.list_price) > 0 ? Number(it.list_price) : (it.unit_price || 0);
    const unitRmb = refUsd ? listUsd : (rmbRate > 0 ? listUsd * rmbRate : 0);
    const lineRmb = refUsd ? usdAmt : (rmbRate > 0 ? usdAmt * rmbRate : 0);
    totalRmb += lineRmb;
    totalUsdAll += usdAmt;
    totalListUsd += listUsd * qty;
    if (mr) {
      const listP = inUsd ? listUsd : listUsd * lineRate;
      return { amt, rmb: lineRmb, usd: usdAmt, listUsd: listUsd * qty, pB, pI, pEx, html: `<tr>
      <td class="nw">${esc(it.product_code || it.code || "")}</td>
      <td class="r nw">${num2(listUsd)}</td><td class="r nw">${num2(usdAmt)}</td>
      <td>${esc(it.product_name || "")}${notaSpec(it) ? " " + esc(notaSpec(it)) : ""}</td>
      <td class="r nw">${qty} ${esc(lineUnit(it))}</td>
      <td class="r nw">${num2(listP)}${discPct > 0 ? ` <span class="nw">-${Number.isInteger(discPct) ? discPct : discPct.toFixed(2)}%</span>` : ""}</td>
      <td class="r nw">${num2(amt)}</td>
    </tr>` };
    }
    return { amt, rmb: lineRmb, usd: usdAmt, pB, pI, pEx, html: `<tr>
      <td class="nw">${esc(it.product_code || it.code || "")}</td>
      ${showRmb ? `<td class="r nw">${refUsd ? fmtBs(unitRmb) : (rmbRate > 0 ? unitRmb.toFixed(2) : "")}</td><td class="c nw">${discPct.toFixed(2).replace(".", ",")} %</td><td class="r nw">${refUsd ? fmtBs(lineRmb) : (rmbRate > 0 ? lineRmb.toFixed(2) : "")}</td>` : ""}
      <td>${esc(it.product_name || "")}${notaSpec(it) ? " " + esc(notaSpec(it)) : ""}${!showRmb && discPct > 0 ? ` <small>(Desc. ${discPct.toFixed(2).replace(".", ",")} %)</small>` : ""}</td>
      <td class="c nw">${qty} &nbsp;${esc(lineUnit(it))}${Number(it.boxes) > 0 ? ` 📦${Number(it.boxes)}cj` : ""}</td>
      <td class="r nw">${fmtBs(unitP)}</td>
      <td class="r nw">${fmtBs(amt)}</td>
    </tr>` };
  });
  const neto = base + exento;
  const total = neto + iva;
  const cur = inUsd ? "$" : "Bs";
  const d = order.created_at ? new Date(order.created_at) : new Date();
  const fecha = mr ? `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}` : d.toLocaleDateString("es-VE");
  const numRaw = String(order.order_no || "").replace(/\D/g, "");
  const num = opts.docNo ? esc(String(opts.docNo)) : (numRaw.slice(-6) || "000001").padStart(6, "0");
  const cond = order.payment_method === "credit"
    ? (Number(order.credit_days) > 0 ? `Credito: ${order.credit_days} Dias` : "Crédito")
    : (order.payment_method === "pedido" ? "" : "Contado");
  // Pre-printed forms already carry the title + red doc number + control number
  // (printed by the imprenta) — software must not duplicate them.
  const ctrl = mr
    ? ""
    : pre
    ? '<div class="ctrl" style="min-height:18px"></div>'
    : isNota
      ? `<div class="ctrl"><span></span><span style="text-align:right">NOTA DE ENTREGA<br/><span class="red">N° ${num}</span></span></div>`
      : isNC
        ? `<div class="ctrl"><span>N° DE CONTROL 00 - <span class="red">${num}</span></span><span>NOTA DE CRÉDITO <span class="red">${num}</span></span></div>`
        : `<div class="ctrl"><span>N° DE CONTROL 00 - <span class="red">${num}</span></span><span>FACTURA <span class="red">${num}</span></span></div>`;
  const legend = f2
    ? `<div class="legend red" style="color:#c00${mr ? ";text-align:left;margin-top:4px" : ""}">${esc(opts.copyLabel || "ORIGINAL - CLIENTE")}</div>`
    : mr
    ? (hid("nf_mr_legend") ? "" : `<div class="legend" style="text-align:left;margin-top:6px">${lbl(settings, "nf_mr_legend", "ESTE FORMA LIBRE VA SIN TACHADURA NI ENMENDADURA")}</div>`)
    : pre ? "" : isNota
    ? '<div class="legend red" style="color:#c00">COPIA-SIN DERECHO A CRÉDITO FISCAL</div>'
    : isNC
      ? '<div class="legend">NOTA DE CRÉDITO — ESTA NOTA VA SIN TACHADURAS NI ENMENDADURAS</div><div class="legend red" style="color:#c00">ORIGINAL - CLIENTE</div>'
      : '<div class="legend">ESTA FACTURA VA SIN TACHADURAS NI ENMENDADURAS</div><div class="legend red" style="color:#c00">ORIGINAL - CLIENTE</div>';
  const theadHtml = mr ? `<thead><tr>
      <th class="nw">${lbl(settings, "nf_h_codigo", "Codigo")}</th><th class="nw"></th><th class="nw"></th>
      <th>${lbl(settings, "nf_h_desc", "Descripcion")}</th><th class="r nw">${lbl(settings, "nf_h_cant", "Cantidad")}</th><th class="r nw">${lbl(settings, "nf_h_precio", "Precio")} ${cur.toUpperCase()}</th><th class="r nw">${lbl(settings, "nf_h_total", "Total")} ${cur.toUpperCase()}</th>
    </tr></thead>` : `<thead><tr>
      <th class="nw">${lbl(settings, "nf_h_codigo", "CODIGO")}</th>
      ${showRmb ? (refUsd ? `<th class="r nw">${lbl(settings, "nf_h_ref", "REF")}</th><th class="c nw">DESC</th><th class="r nw">${lbl(settings, "nf_h_tref", "TOTAL REF")}</th>` : '<th class="r nw">RMB</th><th class="c nw">DESC</th><th class="r nw">T. RMB</th>') : ""}
      <th>${lbl(settings, "nf_h_desc", "DESCRIPCION")}</th><th class="c nw">${lbl(settings, "nf_h_cant", "CANTIDAD")}</th><th class="r nw">${lbl(settings, "nf_h_precio", "PRECIO U")} ${cur.toUpperCase()}</th><th class="r nw">${lbl(settings, "nf_h_total", "TOTAL")} ${cur.toUpperCase()}</th>
    </tr></thead>`;
  // 📄 连续纸分页（Leyenda 2026-10-18 最终版）：每张纸是独立编号的 Nota 表单 →
  // 每页重印客户表头，每页底部打印【本页汇总】（只算本页货品）。无 TRANSPORTE/VIENE（红笔划 ✗）。
  const rpp = Math.max(5, Number(opts.rowsPerPage) || Number(settings?.nf_rows_per_page) || 26);
  const paginated = rowObjs.length > rpp;
  let itemsBlock;
  if (paginated) {
    const topMm = pre ? (Number(settings?.b2b_top_margin_mm) > 0 ? Number(settings.b2b_top_margin_mm) : 28) : 8;
    const pageHdr = `<div style="font-size:11px;line-height:1.4;margin-bottom:1mm">`
      + `<div><b>${lbl(settings, "nf_cliente", "Cliente:")}</b> ${esc(order.customer_name || order.shipping_name || "")} &nbsp; ${hid("nf_rif") ? "" : `<b>${lbl(settings, "nf_rif", "Rif:")}</b> ${esc(order.customer_rif || order.rif || "")}`} &nbsp; ${hid("nf_tel") ? "" : `<b>${lbl(settings, "nf_tel", "Teléfono:")}</b> ${esc(order.customer_phone || order.shipping_phone || "")}`}</div>`
      + `<div><b>${lbl(settings, "nf_direccion", "Dirección:")}</b> ${esc(order.customer_address || order.shipping_address || "")}</div>`
      + `<div>${hid("nf_vendedor") ? "" : `<b>${lbl(settings, "nf_vendedor", "Vendedor:")}</b> ${esc(order.vendedor || order.salesman_name || "")} &nbsp; `}<b>${lbl(settings, "nf_fecha", "Fecha Emisión:")}</b> ${fecha} &nbsp; ${hid("nf_no") ? "" : `<b>${lbl(settings, "nf_no", "N°:")}</b> ${esc(order.order_no || "")}`}${order.notes && !noRepeatRef ? ` &nbsp; <b>${esc(order.notes)}</b>` : ""}</div>`
      + `</div>`;
    const pageFoot = (ch) => {
      const pNeto = ch.reduce((a, r2) => a + r2.pB + r2.pEx, 0);
      const pIva = ch.reduce((a, r2) => a + r2.pI, 0);
      const pRmb = ch.reduce((a, r2) => a + (r2.rmb || 0), 0);
      const pUsd = ch.reduce((a, r2) => a + (r2.usd || 0), 0);
      const pList = ch.reduce((a, r2) => a + (r2.listUsd || 0), 0);
      const totBox = mr ? `<div style="display:flex;justify-content:space-between"><span>${lbl(settings, "nf_neto", `SUBTOTAL ${cur}:`)}</span><span>${num2(pNeto)}</span></div>
          <div style="display:flex;justify-content:space-between"><span>${lbl(settings, "nf_iva", "IMPUESTO 16% :")}</span><span>${num2(pIva)}</span></div>
          <div style="display:flex;justify-content:space-between;font-weight:bold"><span>${lbl(settings, "nf_pagar", `TOTAL ${cur} :`)}</span><span>${num2(pNeto + pIva)}</span></div>` : `<div style="display:flex;justify-content:space-between"><span>${lbl(settings, "nf_neto", "Total Neto:")}</span><span>${fmtBs(pNeto)}</span></div>
          <div style="display:flex;justify-content:space-between"><span>${lbl(settings, "nf_iva", "I.V.A. 16,0 %")}</span><span>${fmtBs(pIva)}</span></div>
          <div style="display:flex;justify-content:space-between;font-weight:bold"><span>${lbl(settings, "nf_pagar", "Total a Pagar")} ${cur}:</span><span>${fmtBs(pNeto + pIva)}</span></div>`;
      return `${mr ? `<div class="mrline" style="margin-top:2mm"></div>` : ""}<div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:2mm;font-size:11px">
        <div>${mr ? `<div>${lbl(settings, "nf_mr_ref", "REFERENCIA:")} ${num2(pList)}</div><div>${lbl(settings, "nf_mr_desc", "Con descuento:")} ${num2(pUsd)}</div><div>${lbl(settings, "nf_mr_items", "CANTIDAD ITEMS:")} ${ch.length}</div>${order.notes ? `<div>${esc(order.notes)}</div>` : ""}` : `<b>${lbl(settings, "nf_obs", "OBSERVACIONES:")}</b>${order.notes ? ` ${esc(order.notes)}` : ""}${order.created_by_name && !hid("nf_elaborado") ? `<br/>${lbl(settings, "nf_elaborado", "Elaborado por")}: ${esc(order.created_by_name)}` : ""}
        ${noNotaTotals ? "" : refUsd ? `<br/><b>${lbl(settings, "nf_tref", "TOTAL REF:")}</b> <u><b>$ ${fmtBs(pUsd)}</b></u>` : (showRmb && rmbRate > 0 ? `<br/><b>${lbl(settings, "nf_trmb", "T RMB:")}</b> <u><b>${fmtBs(pRmb)}</b></u>` : "")}`}</div>
        ${hid("nf_tot") || noNotaTotals ? "" : `<div style="min-width:52mm;font-size:12px">${totBox}</div>`}
      </div>${sorteoDocLine(order)}`;
    };
    const chunks = [];
    for (let i = 0; i < rowObjs.length; i += rpp) chunks.push(rowObjs.slice(i, i + rpp));
    itemsBlock = chunks.map((ch, ci) => {
      const last = ci === chunks.length - 1;
      return (ci > 0 ? `<div style="height:${topMm}mm"></div>${pageHdr}` : "")
        + `<table class="it"${blkAttr(settings, "nf", "table")}>${theadHtml}<tbody>${ch.map(r2 => r2.html).join("")}</tbody></table>`
        + pageFoot(ch)
        + (last ? "" : `<div style="page-break-after:always"></div>`);
    }).join("");
  } else {
    itemsBlock = `<table class="it"${blkAttr(settings, "nf", "table")}>${theadHtml}<tbody>${rowObjs.map(r2 => r2.html).join("")}</tbody></table>`;
  }
  const ctrlAttr = blkAttr(settings, "nf", "ctrl");
  const docLabel = isNota ? lbl(settings, "nf_mr_nota_no", "Nota N°:") : lbl(settings, "nf_mr_fact_no", "Factura N°:");
  const mrHead = pre
    ? `<div style="height:${f2 && Number(settings?.f2_top_mm) > 0 ? Number(settings.f2_top_mm) : Number(settings?.b2b_top_margin_mm) > 0 ? Number(settings.b2b_top_margin_mm) : 28}mm"></div>`
    : `<div class="mrco"${blkAttr(settings, "nf", "co")}>
    <div><h1>${esc((settings?.company_name || settings?.shop_name || "").toUpperCase())}</h1><div class="rif">RIF:${esc(settings?.company_tax_id || "")}</div></div>
    <div class="addr">${esc((settings?.company_address || "").toUpperCase())}</div>
  </div>`;
  const mrCli = `<div class="mrcli"${blkAttr(settings, "nf", "cli")}>
    <div class="l">
      <div><span class="k">${lbl(settings, "nf_cliente", "RAZON SOCIAL:")}</span>${esc(order.customer_name || order.shipping_name || "")}</div>
      <div><span class="k">${lbl(settings, "nf_direccion", "DIRECCION")}</span>${esc(order.customer_address || order.shipping_address || "")}</div>
      <div>${hid("nf_rif") ? "" : `<span class="k">${lbl(settings, "nf_rif", "CI/RIF:")}</span>${esc(order.customer_rif || order.rif || "")}`}
        ${hid("nf_tel") ? "" : `<span class="sp"><span class="k">${lbl(settings, "nf_tel", "TELEFONO:")}</span>${esc(order.customer_phone || order.shipping_phone || "")}</span>`}
        ${hid("nf_vendedor") ? "" : `<span class="sp"><span class="k">${lbl(settings, "nf_vendedor", "VENDEDOR:")}</span>${esc(order.vendedor || order.salesman_name || "")}</span>`}</div>
      ${(hid("nf_codigo") || !order.customer_code) ? "" : `<div><span class="k">${lbl(settings, "nf_codigo", "Codigo:")}</span>${esc(order.customer_code)}</div>`}
    </div>
    <div class="rmeta">
      <div>${lbl(settings, "nf_fecha", "FECHA")} ${fecha}</div>
      <div style="margin-top:14px">${hid("nf_no") || (isFactura && hid("nf_mr_fact_no")) ? "" : `${docLabel}${isNota ? esc(order.order_no || "") : num}`}</div>
      ${cond && !hid("nf_cond") ? `<div>${lbl(settings, "nf_cond", "Condición:")} ${cond}</div>` : ""}
    </div>
  </div>`;
  const mrBot = `<div class="botfix">
  <div class="mrline"></div>
  <div class="botwrap">
    <div class="obs mrobs"${blkAttr(settings, "nf", "obs")}>
      <div>${lbl(settings, "nf_mr_ref", "REFERENCIA:")} ${num2(totalListUsd)}</div>
      <div>${lbl(settings, "nf_mr_desc", "Con descuento:")} ${num2(totalUsdAll)}</div>
      <div>${lbl(settings, "nf_mr_items", "CANTIDAD ITEMS:")} ${rowObjs.length}</div>
      ${settings?.b2b_observaciones ? esc(settings.b2b_observaciones).split("\n").map(l => `<div>${l}</div>`).join("") : ""}
      ${order.notes ? `<div>${esc(order.notes)}</div>` : ""}
      ${legend}
    </div>
    ${hid("nf_tot") ? "" : `<div class="tot mrtot"${blkAttr(settings, "nf", "tot")}>
      <div class="row"><span>${lbl(settings, "nf_neto", `SUBTOTAL ${cur}:`)}</span><span>${num2(neto)}</span></div>
      <div class="row"><span>${lbl(settings, "nf_iva", "IMPUESTO 16% :")}</span><span>${num2(iva)}</span></div>
      <div class="row big"><span>${lbl(settings, "nf_pagar", `TOTAL ${cur} :`)}</span><span>${num2(total)}</span></div>
    </div>`}
  </div>
  </div>`;
  const body = mr ? `${mrHead}${mrCli}${itemsBlock}${paginated ? "" : mrBot}` : `
  ${pre
    ? `<div style="height:${f2 && Number(settings?.f2_top_mm) > 0 ? Number(settings.f2_top_mm) : Number(settings?.b2b_top_margin_mm) > 0 ? Number(settings.b2b_top_margin_mm) : 28}mm"></div>`
    : `<div class="co"${blkAttr(settings, "nf", "co")}>
    <h1>${esc((settings?.company_name || settings?.shop_name || "").toUpperCase())}</h1>
    <div>RIF: ${esc(settings?.company_tax_id || "")}</div>
    <div>${esc(settings?.company_address || "")}</div>
    <div>Telf.: ${esc(settings?.company_phone || "")}</div>
  </div>`}
  ${ctrlAttr ? `<div${ctrlAttr}>${ctrl}</div>` : ctrl}
  <div class="cli"${blkAttr(settings, "nf", "cli")}>
    <div class="l">
      <div><b>${lbl(settings, "nf_cliente", "Cliente:")}</b> ${esc(order.customer_name || order.shipping_name || "")}</div>
      <div><b>${lbl(settings, "nf_direccion", "Dirección:")}</b> ${esc(order.customer_address || order.shipping_address || "")}</div>
    </div>
    <div class="rmeta">
      <div><b>${lbl(settings, "nf_fecha", "Fecha Emisión:")}</b> ${fecha}</div>
      <div><b>${lbl(settings, "nf_cond", "Condición de pago:")}</b> ${cond}</div>
      ${isNC && opts.refFactura ? `<div><b>Fact. afectada:</b> ${esc(opts.refFactura)}</div>` : ""}
    </div>
  </div>
  <div${blkAttr(settings, "nf", "vend")}>
  <div class="vend">
    ${hid("nf_rif") ? "" : `<span><b>${lbl(settings, "nf_rif", "Rif:")}</b> ${esc(order.customer_rif || order.rif || "")}</span>`}
    ${(hid("nf_codigo") || !order.customer_code) ? "" : `<span><b>${lbl(settings, "nf_codigo", "Codigo:")}</b> ${esc(order.customer_code)}</span>`}
    ${hid("nf_tel") ? "" : `<span><b>${lbl(settings, "nf_tel", "Teléfono:")}</b> ${esc(order.customer_phone || order.shipping_phone || "")}</span>`}
  </div>
  <div class="vend">
    ${hid("nf_vendedor") ? "" : `<span><b>${lbl(settings, "nf_vendedor", "Vendedor:")}</b> ${esc(order.vendedor || order.salesman_name || "")}</span>`}
    ${(hid("nf_tlfv") || !order.salesman_phone) ? "" : `<span><b>${lbl(settings, "nf_tlfv", "Tlf. Vend:")}</b> ${esc(order.salesman_phone)}</span>`}
    ${hid("nf_emit") ? "" : `<span><b>${lbl(settings, "nf_emit", "Emitido por:")}</b> ${esc(issuerName())}</span>`}
    ${hid("nf_no") ? "" : `<span><b>${lbl(settings, "nf_no", "N°:")}</b> ${esc(order.order_no || "")}</span>`}
  </div>
  </div>
  ${itemsBlock}
  ${paginated ? "" : `<div class="botfix">
  <div class="botwrap">
    <div class="obs"${blkAttr(settings, "nf", "obs")}>
      <div><b>${lbl(settings, "nf_obs", "OBSERVACIONES:")}</b></div>
      ${settings?.b2b_observaciones ? esc(settings.b2b_observaciones).split("\n").map(l => `<div>${l}</div>`).join("") : ""}
      ${order.notes ? `<div>${esc(order.notes)}</div>` : ""}
      ${!noNotaTotals && refUsd ? `<div style="margin-top:4px;font-size:13px"><b>${lbl(settings, "nf_tref", "TOTAL REF:")}</b> <u><b>$ ${fmtBs(totalUsdAll)}</b></u></div>` : ""}
      ${!noNotaTotals && showRmb && !refUsd && rmbRate > 0 ? `<div style="margin-top:4px;font-size:${rmbOnly ? "14px" : "inherit"}"><b>${lbl(settings, "nf_trmb", "T RMB:")}</b> <u><b>${fmtBs(totalRmb)}</b></u>${rmbOnly ? "" : ` &nbsp;&nbsp; <b>USD:</b> <u>$${totalUsdAll.toFixed(2)}</u>`}</div>` : ""}
      ${!noNotaTotals && isNota && (settings?.b2b_nota_legend ?? "NO NOS HACEMOS RESPONSABLES POR PAGO EN EFECTIVO A VENDEDORES") ? `<div style="margin-top:4px">${esc(settings?.b2b_nota_legend ?? "NO NOS HACEMOS RESPONSABLES POR PAGO EN EFECTIVO A VENDEDORES")}</div>` : ""}
    </div>
  ${f2 ? "" : shopQrHtml(settings)}
    ${hid("nf_tot") || noNotaTotals ? "" : `<div class="tot"${blkAttr(settings, "nf", "tot")}>
      <div class="row"><span>${lbl(settings, "nf_neto", "Total Neto:")}</span><span>${fmtBs(neto)}</span></div>
      <div class="row"><span>${lbl(settings, "nf_iva", "I.V.A. 16,0 %")}</span><span>${fmtBs(iva)}</span></div>
      <div class="row big"><span>${lbl(settings, "nf_pagar", "Total a Pagar")} ${cur}:</span><span>${fmtBs(total)}</span></div>
    </div>`}
  </div>
  ${legend ? `<div${blkAttr(settings, "nf", "legend")}>${legend}</div>` : ""}
  </div>`}`;
  const mL = Number(settings?.b2b_margin_left_mm), mR = Number(settings?.b2b_margin_right_mm);
  const marginCss = (mL > 0 || mR > 0) ? `<style>body{padding:10mm ${mR > 0 ? mR : 12}mm 10mm ${mL > 0 ? mL : 12}mm}</style>` : "";
  // 📏 Paper per document (tenant choice): nf_paper (Nota/NC) / factura_paper (Factura).
  // letter (default) | a4 | a5 | half | auto (= printer's own paper, no size declared → no Canon "paper mismatch").
  // nf_page_h_mm > 0 (continuous pre-printed form) always wins.
  const pgH = Number(settings?.nf_page_h_mm) || 0;
  const paperKey = (isFactura ? settings?.factura_paper : settings?.nf_paper) || "letter";
  let pageCss;
  if (pgH >= 60) pageCss = `<meta name="sellox-page-h" content="${pgH}"><style>@page{size:216mm ${pgH}mm;margin:0}</style>`;
  else if (paperKey === "auto") pageCss = `<style>@page{size:auto;margin:0}</style>`;
  else { const p = DOC_PAPER[paperKey] || DOC_PAPER.letter; pageCss = `<meta name="sellox-page-size" content="${p.mm}"><style>@page{size:${p.css};margin:0}</style>`; }
  // 🔠 Nota/Factura font size in px (Leyenda 2026-10-14 "no hay 14"): scales body/table/header proportionally from the 12px base
  const nfPt = Number(settings?.nf_font_size);
  const nfSizeCss = nfPt >= 9 && nfPt <= 24 && nfPt !== 12 ? `<style>body{font-size:${nfPt}px} table.items th{font-size:${Math.round(nfPt * 11 / 12)}px} table.items td{font-size:${nfPt}px} .hdr .meta,.cust{font-size:${nfPt}px} .hdr h1{font-size:${Math.round(nfPt * 20 / 12)}px} .doc-title{font-size:${Math.round(nfPt * 15 / 12)}px}</style>` : "";
  // Factura 2: lift the totals block above the pre-printed footer (default 40mm from the bottom edge)
  // Pre-printed footer (M&R Factura: "Original / Elaborado por…" strip = 25mm) → totals block sits above it (+10mm breathing room)
  const botMm = Number(settings?.b2b_bottom_margin_mm) > 0 ? Number(settings.b2b_bottom_margin_mm) + 10 : 0;
  const f2Css = f2 ? `<style>.botfix{bottom:${Number(settings?.f2_bottom_mm) > 0 ? Number(settings.f2_bottom_mm) : 40}mm}</style>` : (isFactura && pre && botMm > 0 ? `<style>.botfix{bottom:${botMm}mm}</style>` : "");
  return "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><style>" + NF_CSS + "</style>" + pageCss + marginCss + fontCss(settings) + nfSizeCss + f2Css + "</head><body>" + body + "</body></html>";
}

// 🧾 Factura 2 — laser, pre-printed form, N copies (ORIGINAL - CLIENTE, COPIA…). Never goes through the dot-matrix TXT path.
export async function printFactura2(order, settings, rates, opts, printOpts) {
  const copies = Math.min(4, Math.max(1, Number(settings?.f2_copies) || 2));
  const labels = ["ORIGINAL - CLIENTE", "COPIA", "COPIA 2", "COPIA 3"];
  const printer = printOpts?.printerName || settings?.report_printer_name || "";
  for (let i = 0; i < copies; i++) {
    const html = buildNotaFacturaHtml(order, settings, rates, "factura", 0, { ...(opts || {}), preprinted: true, factura2: true, copyLabel: labels[i] });
    const dialogPrint = () => {
      const w = window.open("", "_blank", "width=900,height=1000");
      if (!w) return;
      w.document.write(html); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.error("F2 print:", e); } }, 400);
    };
    if (typeof window !== "undefined" && window.selloxDesktop?.printHTML) {
      try { const r = await window.selloxDesktop.printHTML(html, printer); if (!r || !r.ok) dialogPrint(); } catch (e) { dialogPrint(); }
    } else dialogPrint();
  }
}

// 🖨️ Per-device toggle: dot-matrix native TEXT mode (Epson LX-350) — sharp
// impact printing via raw ESC/P instead of fuzzy rendered bitmaps.
// Raw ESC/P only makes sense on an impact printer: a Canon inkjet or a thermal
// printer silently swallows it ("no reaction"). So the toggle applies only when
// the TARGET printer is the dot-matrix one (name looks like Epson LX/FX/LQ, or it
// is the configured report printer); every other printer keeps HTML printing.
const DOT_MATRIX_RE = /\b(LX|FX|LQ|DFX|MX|TM-U)[- ]?\d|ESC\/?P|matri[zx]|dot[- ]?matrix|impact|impresora de impacto|OKI\s*ML|KX-P/i;
export const isDotMatrixPrinter = (name, settings) => {
  const n = String(name || "").trim();
  if (!n) return false;
  const rep = String(settings?.report_printer_name || "").trim();
  return DOT_MATRIX_RE.test(n) || (!!rep && n.toLowerCase().endsWith(rep.toLowerCase()) && DOT_MATRIX_RE.test(rep));
};
export const dotMatrixTxtOn = (printerName, settings) => {
  try {
    if (localStorage.getItem("sellox_dot_matrix_txt") !== "1" || !window.selloxDesktop?.printRaw) return false;
    // no explicit printer → goes to the report printer (the toggle lives in that block)
    return isDotMatrixPrinter(printerName || settings?.report_printer_name, settings);
  } catch (e) { return false; }
};

export function printNotaFactura(order, settings, rates, docType, rmbRate, opts, printOpts) {
  if (dotMatrixTxtOn(printOpts?.printerName, settings)) {
    import("./dotMatrixText").then(m => m.printNotaFacturaTxt(order, settings, rates, docType, rmbRate, opts, { ...(printOpts || {}), printerName: printOpts?.printerName || settings?.report_printer_name || "" })
      .catch(async (e) => { const { busToast } = await import("./toastBus"); busToast("error", "🖨️ TXT: " + (e.message || e)); }));
    return;
  }
  const html = buildNotaFacturaHtml(order, settings, rates, docType, rmbRate, opts || {});
  const dialogPrint = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.error("NF print:", e); } }, 400);
  };
  // Desktop: always print through the shell (named printer or Windows default) so the exact paper size
  // declared in the HTML reaches the driver (A5 stays A5 — no centering on Letter). Dialog only as fallback.
  if (typeof window !== "undefined" && window.selloxDesktop?.printHTML) {
    window.selloxDesktop.printHTML(html, printOpts?.printerName || settings?.report_printer_name || "")
      .then((r) => { if (!r || !r.ok) dialogPrint(); }).catch(dialogPrint);
    return;
  }
  dialogPrint();
}

// Pack unit encoded in the SKU label ("BTx24CJ" → BT, "CJx12PZ" → CJ) wins over the product unit:
// orders placed before the SKU unit column was filled still carry the product unit (Leyenda 2026-10-03).
export function lineUnit(it) {
  const m = String(lineVariant(it)).match(/(?:^|[\s(:·/])(BT|CJ|PZ|PQ|PAQ|UD|UND|DOC|DOZ|BOLSA|SACO|CAJA|BULTO)\s*[x×]\s*\d+/i);
  return ((m ? m[1] : it?.unit) || "PZ").toUpperCase();
}

// 🎨 Merge SKU/variant lines of the same product (same unit price) into one printed row:
// "Esmalte X" qty=240, variant="12 colores: Rojo×20 · Rosa×30 …" → long color lists stop eating pages.
export function mergeVariantLines(order) {
  const items = order?.items || [];
  const groups = new Map(); const out = [];
  items.forEach(it => {
    const variant = lineVariant(it);
    if (!it.product_id || !variant) { out.push(it); return; }
    const price = Number(it.unit_price ?? it.price ?? 0);
    const k = `${it.product_id}|${price}|${lineUnit(it)}`;
    let g = groups.get(k);
    if (!g) {
      const base = String(it.product_name || "").replace(new RegExp("\\s*\\(" + variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\)\\s*$"), "");
      g = { ...it, unit: lineUnit(it), product_name: base, quantity: 0, amount: 0, amount_bs: 0, variant: "", variants: "", product_spec: "", sku_key: undefined, sku_label: undefined, _parts: [] };
      groups.set(k, g); out.push(g);
    }
    const q = Number(it.quantity) || 0;
    g.quantity += q; g.amount += Number(it.amount) || q * price; g.amount_bs += Number(it.amount_bs) || 0;
    g._parts.push(`${variant}/${q}`);
  });
  // Leyenda 红笔 2026-10-19: ASCII-safe "52/1  50/1" (× and · vanish on dot-matrix → "521"), no "N var.:" prefix
  out.forEach(g => { if (g._parts) { g.variant = g._parts.length === 1 ? g._parts[0].replace(/\/\d+(?:\.\d+)?$/, "") : g._parts.join("  "); delete g._parts; } });
  return { ...order, items: out };
}
